import { pluralEn } from '../../plural';
import type { players as ru } from '../ru/players';

const matchesWord = (n: number): string => pluralEn(n, 'match', 'matches');

export const players: typeof ru = {
  status: {
    active: 'Active',
    banned: 'Banned'
  },
  reasonQuoted: (reason: string) => `"${reason}"`,
  forever: 'permanent',
  until: (date: string) => `until ${date}`,
  pagination: {
    back: '← Back',
    next: 'Next →',
    info: (page: number, totalPages: number, total: number) => `Page ${page} / ${totalPages} · ${total} total`
  },
  tagAssignment: {
    kicker: 'Player',
    title: 'Assign tag',
    assignLabel: 'Assign tag',
    noTag: 'No tag',
    save: 'Save tag',
    clear: 'Remove',
    selectHint: 'Select a player in the table to assign or remove a tag.',
    failedMarker: 'Failed'
  },
  filters: {
    title: 'Players',
    total: (n: number) => `${n} total`,
    namePlaceholder: 'Name / Steam',
    banned: 'Banned',
    active: 'Active'
  },
  table: {
    player: 'Player',
    rating: 'Rating',
    matches: 'Matches',
    total: 'total',
    tag: 'Tag',
    status: 'Status',
    loading: 'Loading players...',
    empty: 'No players match the current filters.',
    next: 'Next',
    summary: (page: number, totalPages: number, remaining: number, total: number) =>
      `Page ${page} / ${totalPages}, ${remaining} left, ${total} total`
  },
  profile: {
    weekdaysShort: ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'],
    ongoing: 'live',
    durationHoursMinutes: (hours: number, minutes: number) => `${hours}h ${minutes}m`,
    durationMinutes: (minutes: number) => `${minutes}m`,
    justNow: 'just now',
    minutesAgo: (minutes: number) => `${minutes} min ago`,
    hoursAgo: (hours: number) => `${hours} h ago`,
    daysAgo: (days: number) => `${days} d ago`,
    monthsAgo: (months: number) => `${months} mo ago`,
    yearsAgo: (years: number) => `${years} ${pluralEn(years, 'year', 'years')} ago`,
    trend7d: (delta: number) => `${delta > 0 ? '+' : ''}${delta} in 7 days`,
    noChange7d: 'no change in 7 days',
    ratingKicker: (mode: string) => `${mode} rating`,
    ratingKickerUpper: (mode: string) => `${mode} RATING`,
    rankInMode: (rank: number, mode: string) => `#${rank} in ${mode}`,
    wins: 'wins',
    top3: 'top-3',
    weekTitle: 'This week',
    weekMatches: (n: number) => `${n} ${matchesWord(n)}`,
    weekAvgPlace: (place: string) => ` · avg place ${place}`
  },
  detail: {
    copyTitle: (label: string) => `Copy ${label}`,
    loadingShort: 'Loading...',
    topListEmptyDescription: 'Data will appear after the first matches.',
    matchesCount: (n: number) => `${n} ${matchesWord(n)}`,
    stateUnavailable: 'State unavailable',
    live: 'live',
    playersCount: (n: number) => `${n} ${pluralEn(n, 'player', 'players')}`,
    activeMatch: 'Active match',
    round: (round: number) => ` · Round ${round}`,
    yourHero: 'Your hero',
    place: (place: number) => ` · place ${place}`,
    openMatch: 'Open match',
    copyId: 'Copy ID',
    loadingPlayer: 'Loading player profile...',
    loadErrorTitle: 'Failed to load',
    toSearch: 'To player search',
    notFound: 'Player not found',
    notFoundDescription: 'Check the UID or open the player from the list.',
    backToSearch: '← To player search',
    fetchFailed: 'Failed to load player profile.',
    tagsLoadFailed: 'Failed to load tags.',
    noSubscription: 'No subscription',
    vipBadge: 'VIP',
    vipBadgeTitle: 'VIP subscriber',
    subscription: 'Subscription',
    subscriptionUntil: (date: string) => `Subscription until ${date}`,
    inMatch: (id: string) => `In match · #${id}`,
    balance: 'Balance',
    lastSeen: 'Last seen',
    joined: (date: string) => `Joined ${date}`,
    steamProfile: 'Steam profile',
    bansCount: (n: number) => `Bans ${n}`,
    lastBan: (date: string) => ` · last ${date}`,
    noBans: 'No bans',
    kpiTotalMatches: 'Total matches',
    kpiAvgPlace: 'Avg place',
    kpiAvgRounds: 'Avg rounds',
    favHeroes: 'Favorite heroes',
    favAbilities: 'Favorite abilities',
    recentMatches: 'Recent matches',
    allMatches: 'All matches →',
    loadingMatches: 'Loading matches...',
    noMatches: 'No matches',
    noMatchesDescription: 'This player has not played any matches yet.',
    banned: 'Player banned.',
    unbanned: 'Ban lifted.'
  },
  vipReminder: {
    expiresToday: 'VIP expires today',
    expiresIn: (days: number) => `VIP expires in ${days} ${pluralEn(days, 'day', 'days')}`,
    expired: (days: number) => `VIP expired ${days} ${pluralEn(days, 'day', 'days')} ago`,
    hintExpiring: 'Renew your subscription to keep your bonuses.',
    hintExpired: 'Renew your subscription to get your bonuses back.',
    renew: 'Renew'
  },
  moderation: {
    kicker: 'Moderation',
    menu: 'Moderator actions',
    assignTag: 'Assign tag',
    ban: 'Ban',
    unban: 'Unban',
    banHistory: 'Ban history',
    subscriptionActivate: 'Activate subscription',
    subscriptionExtend: 'Extend subscription',
    subscriptionRevoke: 'Revoke subscription'
  },
  revokeSubscriptionModal: {
    kicker: 'Action',
    title: 'Revoke subscription',
    confirmPrefix: 'Revoke the subscription of player',
    confirmSuffix: '?',
    activeUntil: (date: string) => `It is currently active until ${date}.`,
    submit: 'Revoke subscription',
    submitting: 'Revoking…',
    failed: 'Failed to revoke the subscription.',
    done: 'Subscription revoked.'
  },
  subscriptionModal: {
    dialogLabel: 'Player subscription',
    titleActivate: (name: string) => `Activate subscription: ${name}`,
    titleExtend: (name: string) => `Extend subscription: ${name}`,
    statusActive: (date: string) => `Subscription active until ${date}`,
    statusNone: 'No subscription',
    duration: 'Duration',
    durationLabel: 'Subscription duration',
    customUntil: 'Subscription until',
    submitActivate: 'Activate',
    submitExtend: 'Extend',
    submitting: 'Saving…',
    failed: 'Failed to update the subscription.',
    done: 'Subscription updated.',
    presets: {
      day7: '7 days',
      day30: '30 days',
      day90: '90 days',
      custom: 'Until date'
    }
  },
  tagModal: {
    currentTag: 'Current tag',
    notAssigned: 'none',
    clearTag: 'Remove tag',
    playerNotFound: 'Player not found.',
    assigning: 'Assigning tag...',
    clearing: 'Removing tag...',
    updated: 'Tag updated.',
    cleared: 'Tag removed.',
    updateFailed: 'Failed to update the player tag.'
  },
  banModal: {
    dialogLabel: 'Ban player',
    title: (name: string) => `Ban ${name}`,
    reason: 'Reason',
    reasonPlaceholder: 'E.g. cheating, chat abuse',
    duration: 'Duration',
    durationLabel: 'Ban duration',
    customUntil: 'Banned until',
    submit: 'Ban',
    submitting: 'Banning…',
    failed: 'Failed to ban the player.',
    presets: {
      day1: '1 day',
      day3: '3 days',
      day7: '7 days',
      day30: '30 days',
      forever: 'Permanent',
      custom: 'Until date'
    }
  },
  unbanModal: {
    kicker: 'Action',
    title: 'Unban',
    confirmPrefix: 'Unban player',
    confirmSuffix: '?',
    submit: 'Unban',
    submitting: 'Unbanning…',
    failed: 'Failed to lift the ban.'
  },
  banHistory: {
    heading: (name: string) => `Ban history — ${name}`,
    loading: 'Loading history...',
    errorTitle: 'Failed to load',
    emptyTitle: 'No bans',
    emptyDescription: 'This player has never been banned.',
    status: {
      ACTIVE: 'Active',
      EXPIRED: 'Expired',
      UNBANNED: 'Lifted',
      REPLACED: 'Replaced'
    },
    bannedBy: (by: string, at: string) => `Banned by ${by} · ${at}`,
    unbannedBy: (by: string, at: string) => `Lifted by ${by} · ${at}`,
    replacedBy: (by: string, at: string) => `Replaced by a new ban from ${by} · ${at}`,
    loadMore: 'Show more',
    loadingMore: 'Loading…',
    failed: 'Failed to load ban history.'
  },
  list: {
    title: 'Players',
    searchPlaceholder: 'Name or uid...',
    searchAria: 'Search by name or uid',
    statusAria: 'Status',
    statusAll: 'Status: All',
    everBannedAria: 'Ban history',
    everBannedAll: 'Bans: All',
    everBanned: 'Ever banned',
    neverBanned: 'Never banned',
    modeAria: 'Mode',
    modeTitle: 'Rating and matches are shown for the selected mode',
    mode: (label: string) => `Mode: ${label}`,
    resetFilters: 'Reset filters',
    sortAria: 'Sorting',
    sort: {
      ratingDesc: 'Sort: Rating ↓',
      ratingAsc: 'Sort: Rating ↑',
      matchesDesc: 'Sort: Matches ↓',
      matchesAsc: 'Sort: Matches ↑',
      balanceDesc: 'Sort: Balance ↓',
      balanceAsc: 'Sort: Balance ↑',
      nameAsc: 'Sort: Name ↑',
      nameDesc: 'Sort: Name ↓'
    },
    topTitle: 'TOP RATING THIS WEEK',
    fullTop: 'Full top →',
    found: (n: number) => `${n} ${pluralEn(n, 'player', 'players')} found`,
    pageOf: (page: number, totalPages: number) => `page ${page} of ${totalPages}`,
    columns: {
      rank: 'Place',
      player: 'Player',
      tags: 'Tags',
      status: 'Status',
      subscription: 'Subscription',
      rating: 'Rating',
      matches: 'Matches',
      balance: 'Balance'
    },
    sortByRating: 'Sort by rating',
    sortByMatches: 'Sort by matches',
    sortByBalance: 'Sort by balance',
    loadingAria: 'Loading players',
    errorTitle: 'Failed to load',
    emptyTitle: 'No players found',
    emptyDescription: 'Try changing the filters',
    loadFailed: 'Failed to load players.',
    openProfileAria: (name: string) => `Open profile of ${name}`,
    profileAria: (name: string) => `Profile of ${name}`,
    matchesCount: (n: number) => `${n} ${matchesWord(n)}`,
    noTag: 'no tag',
    you: 'You',
    subscriptionActive: 'Active',
    subscriptionNone: 'None',
    profile: 'Profile',
    actionsAria: 'Actions',
    openProfile: 'Open profile',
    banTrigger: 'Ban',
    wasBanned: (n: number) => `was banned ×${n}`,
    banNow: 'Now:',
    banSummary: (count: number, lastDate: string) => `Bans: ${count} · last: ${lastDate}`
  },
  statistics: {
    title: 'Statistics',
    modeAll: 'All modes',
    playerFilterAll: 'Player: All',
    playerFilterTitle: 'TODO Phase 2: player filter',
    errorTitle: 'Failed to load',
    loadFailed: 'Failed to load statistics.',
    totalMatches: 'Total matches',
    perSeason: 'this season',
    topHeroes: 'Top heroes',
    topAbilities: 'Top abilities',
    byBanRate: 'by ban rate',
    byPickRate: 'by pick rate',
    columns: {
      name: 'Name',
      matches: 'Matches',
      bans: 'Bans',
      picks: 'Picks',
      avgPlace: 'Avg place'
    },
    loadingAria: 'Loading statistics',
    emptyDescription: 'No data for the selected mode.',
    topItems: 'Top items',
    itemsSub: 'all modes',
    itemsLoadFailed: 'Failed to load item statistics.',
    detail: {
      back: '← Back to statistics',
      kind: { hero: 'Hero', ability: 'Ability', item: 'Item' },
      kpi: { matches: 'Matches', pickRate: 'Pick rate', avgPlace: 'Avg place', avgRounds: 'Avg rounds' },
      sections: {
        items: 'Popular items',
        abilities: 'Popular abilities',
        heroes: 'Most picked on heroes'
      },
      columns: {
        share: {
          hero: 'Share of hero matches',
          ability: 'Share of ability matches',
          item: 'Share of item matches'
        },
        avgPlace: {
          hero: 'Avg place with this hero',
          ability: 'Avg place with this ability',
          item: 'Avg place with this item'
        }
      },
      loadingAria: 'Loading detailed statistics',
      emptyEntries: 'No data yet.',
      errorTitle: 'Failed to load',
      loadFailed: 'Failed to load detailed statistics.',
      notFoundTitle: 'Nothing found',
      notFoundDescription: 'There are no statistics for this name; the link may be outdated.',
      recentMatches: {
        title: 'Recent matches',
        columns: { date: 'Date', type: 'Mode', player: 'Player', place: 'Place', rating: 'Rating', match: 'Match' },
        matchLink: (matchId: string) => `Match #${matchId}`,
        unknownPlayer: (uid: string) => `Player ${uid}`,
        loadingAria: 'Loading recent matches',
        emptyDescription: 'No finished matches with this hero yet.',
        loadFailed: 'Failed to load recent matches.'
      }
    }
  },
  banPresets: {
    title: 'Ban presets',
    readOnly: 'Preset editing is unavailable',
    loadFailed: 'Failed to load account data.',
    slotsUsed: (n: number, max: number) => `${n} of ${max}`,
    slotsAria: (n: number, max: number) => `Slots used: ${n} of ${max}`,
    listAria: 'Preset list',
    rowReorderTitle: 'Alt+↑/↓ — change order',
    rowAria: (position: number, name: string) => `Preset ${position}: ${name}`,
    unnamed: 'Untitled',
    emptyPreset: 'Empty',
    heroesCount: (n: number) => `${n} ${pluralEn(n, 'hero', 'heroes')}`,
    abilitiesCount: (n: number) => `${n} ${pluralEn(n, 'ability', 'abilities')}`,
    newPreset: 'New preset',
    newPresetName: 'New preset',
    railHint: 'During the ban phase presets appear in this same order — drag to change it. One click bans the whole set.',
    nameLabel: 'Preset name',
    namePlaceholder: 'Preset name',
    duplicate: 'Duplicate',
    delete: 'Delete',
    copyName: (name: string) => `${name} (copy)`,
    deleteConfirm: (name: string) => `Delete preset "${name}"?`,
    heroesTitle: 'Heroes',
    abilitiesTitle: 'Abilities',
    clear: 'Clear',
    clearAria: (title: string) => `Clear: ${title}`,
    emptyHeroes: 'Pick heroes in the catalog below',
    emptyAbilities: 'Pick abilities in the catalog below',
    orderHint: 'Order is priority: drag an icon to change it. In game the first ones within the limit are banned first; the rest only if the earlier ones were already banned by someone.',
    orderHintKeys: 'Keyboard: Alt+←/→.',
    priorityAria: (position: number, name: string) => `Priority ${position}: ${name}`,
    removeAria: (name: string) => `Remove ${name}`,
    catalogTabsAria: 'Catalog',
    catalogHeroes: 'Heroes',
    catalogAbilities: 'Abilities',
    searchLabel: 'Search the catalog',
    searchPlaceholder: 'Search by name',
    onlySelected: 'In preset only',
    nothingFound: 'Nothing found',
    emptyTitle: 'No presets yet',
    emptyDescription: 'Create your first preset: it will show up in the quick-pick panel during the ban phase.',
    emptyReadOnly: 'No presets yet.',
    unsavedChanges: 'You have unsaved changes',
    revert: 'Revert',
    save: 'Save',
    saving: 'Saving...',
    blankName: (position: number) => `Enter a name for preset #${position}`,
    saved: 'Saved',
    saveFailed: 'Failed to save.'
  }
};
